/**
 * StatBlockViewer - Traditional D&D Stat Block Display
 *
 * Renders monster data in authentic D&D book format:
 * - Single-column layout like official stat blocks
 * - Traditional typography and spacing
 * - All information in one unified display
 *
 * @date_of_change 2025-09-29 - Created traditional D&D stat block viewer
 */

import React from 'react';
import './StatBlockViewer.css';

const StatBlockViewer = ({ monster }) => {
  if (!monster) {
    return (
      <div className="stat-block">
        <div className="stat-block-placeholder">
          No monster data available
        </div>
      </div>
    );
  }

  // Helper functions
  const calculateModifier = (score) => {
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  const formatAbilityScore = (score, ability) => {
    const value = score || 10;
    return `${value} (${calculateModifier(value)})`;
  };

  const formatSpeed = (speed) => {
    if (typeof speed === 'object') {
      return Object.entries(speed)
        .map(([type, value]) => type === 'walk' ? `${value} ft.` : `${type} ${value} ft.`)
        .join(', ');
    }
    return `${speed || 30} ft.`;
  };

  const formatList = (items) => {
    if (!items || (Array.isArray(items) && items.length === 0)) return '—';
    if (Array.isArray(items)) return items.join(', ');
    return items;
  };

  const formatSenses = (senses) => {
    if (!senses) return 'passive Perception 10';
    if (typeof senses === 'object') {
      const senseList = Object.entries(senses)
        .map(([key, value]) => {
          if (key === 'passivePerception') return `passive Perception ${value}`;
          return `${key} ${value} ft.`;
        })
        .join(', ');
      return senseList || 'passive Perception 10';
    }
    return senses;
  };

  const formatLanguages = (languages) => {
    if (!languages) return '—';
    if (typeof languages === 'object') {
      const langList = languages.list || [];
      const telepathy = languages.telepathy ? `telepathy ${languages.telepathy} ft.` : '';
      return [...langList, telepathy].filter(Boolean).join(', ') || '—';
    }
    return languages;
  };

  const formatCR = (cr) => {
    const crValue = cr || '1/4';
    // Calculate XP based on CR
    const xpTable = {
      '0': 10, '1/8': 25, '1/4': 50, '1/2': 100,
      '1': 200, '2': 450, '3': 700, '4': 1100, '5': 1800,
      '6': 2300, '7': 2900, '8': 3900, '9': 5000, '10': 5900,
      '11': 7200, '12': 8400, '13': 10000, '14': 11500, '15': 13000,
      '16': 15000, '17': 18000, '18': 20000, '19': 22000, '20': 25000,
      '21': 33000, '22': 41000, '23': 50000, '24': 62000, '25': 75000,
      '26': 90000, '27': 105000, '28': 120000, '29': 135000, '30': 155000
    };
    const xp = xpTable[crValue] || 50;
    return `${crValue} (${xp.toLocaleString()} XP)`;
  };

  const renderActions = (actions, title = "Actions") => {
    if (!actions || actions.length === 0) return null;

    return (
      <div className="stat-block-section">
        <h4 className="stat-block-section-title">{title}</h4>
        {actions.map((action, index) => (
          <div key={index} className="stat-block-action">
            <p className="stat-block-action-name">
              <strong><em>{action.name}</em></strong>
              {action.recharge && (
                <span className="recharge"> ({action.recharge})</span>
              )}
            </p>
            <p className="stat-block-action-description">
              {action.text || action.description || (
                action.attack ?
                  `${action.attack.type || 'Attack'}: ${action.attack.toHit ? `+${action.attack.toHit} to hit, ` : ''}${action.attack.reach ? `reach ${action.attack.reach} ft., ` : ''}${action.attack.range ? `range ${action.attack.range} ft., ` : ''}one target. ${action.attack.damage ? `Hit: ${action.attack.damage.map(d => `${d.dice} ${d.type} damage`).join(' plus ')}.` : ''}`
                  : 'No description available.'
              )}
            </p>
          </div>
        ))}
      </div>
    );
  };

  const renderTraits = (traits) => {
    if (!traits || traits.length === 0) return null;

    return (
      <div className="stat-block-section">
        {traits.map((trait, index) => (
          <div key={index} className="stat-block-trait">
            <p>
              <strong><em>{trait.name}</em></strong>. {trait.text || trait.description || 'No description available.'}
            </p>
          </div>
        ))}
      </div>
    );
  };

  // Extract data with fallbacks
  const abilities = monster.abilities || {};
  const hp = monster.hp || monster.maxHp || 1;
  const hitDice = monster.hitDice || monster.hp?.formula || '';
  const ac = monster.ac || 10;
  const speed = formatSpeed(monster.speed);
  const cr = formatCR(monster.cr || monster.challengeRating);

  return (
    <div className="stat-block">
      <div className="stat-block-content">
        {/* Header */}
        <div className="stat-block-header">
          <h2 className="stat-block-name">{monster.name || 'Unknown Monster'}</h2>
          <p className="stat-block-meta">
            <em>{monster.size || 'Medium'} {monster.monsterType || monster.type || 'humanoid'}, {monster.alignment || 'neutral'}</em>
          </p>
        </div>

        <hr className="stat-block-divider" />

        {/* Basic Stats */}
        <div className="stat-block-basics">
          <p><strong>Armor Class</strong> {ac}</p>
          <p><strong>Hit Points</strong> {hp} {hitDice && `(${hitDice})`}</p>
          <p><strong>Speed</strong> {speed}</p>
        </div>

        <hr className="stat-block-divider" />

        {/* Ability Scores */}
        <div className="stat-block-abilities">
          <div className="ability-grid">
            <div className="ability">
              <strong>STR</strong><br />
              {formatAbilityScore(abilities.strength || abilities.str, 'str')}
            </div>
            <div className="ability">
              <strong>DEX</strong><br />
              {formatAbilityScore(abilities.dexterity || abilities.dex, 'dex')}
            </div>
            <div className="ability">
              <strong>CON</strong><br />
              {formatAbilityScore(abilities.constitution || abilities.con, 'con')}
            </div>
            <div className="ability">
              <strong>INT</strong><br />
              {formatAbilityScore(abilities.intelligence || abilities.int, 'int')}
            </div>
            <div className="ability">
              <strong>WIS</strong><br />
              {formatAbilityScore(abilities.wisdom || abilities.wis, 'wis')}
            </div>
            <div className="ability">
              <strong>CHA</strong><br />
              {formatAbilityScore(abilities.charisma || abilities.cha, 'cha')}
            </div>
          </div>
        </div>

        <hr className="stat-block-divider" />

        {/* Secondary Stats */}
        <div className="stat-block-secondary">
          {monster.savingThrows && Object.keys(monster.savingThrows).length > 0 && (
            <p><strong>Saving Throws</strong> {
              Object.entries(monster.savingThrows)
                .map(([ability, bonus]) => `${ability.substring(0, 3).toUpperCase()} +${bonus}`)
                .join(', ')
            }</p>
          )}

          {monster.skills && Object.keys(monster.skills).length > 0 && (
            <p><strong>Skills</strong> {
              Object.entries(monster.skills)
                .map(([skill, bonus]) => `${skill.charAt(0).toUpperCase() + skill.slice(1)} +${bonus}`)
                .join(', ')
            }</p>
          )}

          {monster.damageVulnerabilities && monster.damageVulnerabilities.length > 0 && (
            <p><strong>Damage Vulnerabilities</strong> {formatList(monster.damageVulnerabilities)}</p>
          )}

          {monster.damageResistances && monster.damageResistances.length > 0 && (
            <p><strong>Damage Resistances</strong> {formatList(monster.damageResistances)}</p>
          )}

          {monster.damageImmunities && monster.damageImmunities.length > 0 && (
            <p><strong>Damage Immunities</strong> {formatList(monster.damageImmunities)}</p>
          )}

          {monster.conditionImmunities && monster.conditionImmunities.length > 0 && (
            <p><strong>Condition Immunities</strong> {formatList(monster.conditionImmunities)}</p>
          )}

          <p><strong>Senses</strong> {formatSenses(monster.senses)}</p>
          <p><strong>Languages</strong> {formatLanguages(monster.languages)}</p>
          <p><strong>Challenge Rating</strong> {cr}</p>
        </div>

        <hr className="stat-block-divider" />

        {/* Traits */}
        {renderTraits(monster.traits)}

        {/* Actions */}
        {renderActions(monster.actions, "Actions")}

        {/* Reactions */}
        {renderActions(monster.reactions, "Reactions")}

        {/* Legendary Actions */}
        {monster.legendaryActions && (
          <div className="stat-block-section">
            <h4 className="stat-block-section-title">Legendary Actions</h4>
            {monster.legendaryActions.text && (
              <p className="legendary-description">{monster.legendaryActions.text}</p>
            )}
            {monster.legendaryActions.options && monster.legendaryActions.options.map((action, index) => (
              <div key={index} className="stat-block-action">
                <p>
                  <strong><em>{action.name}</em></strong>
                  {action.cost && action.cost > 1 && ` (Costs ${action.cost} Actions)`}. {action.text || action.description}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StatBlockViewer;